import { Injectable } from '@angular/core';
import {
  LoyaltyAttributeDefinitionDto,
  LoyaltyAttributeDefinitionQuestionDto,
  LoyaltyEnrollment,
  LoyaltyEnrollmentStatus,
  LoyaltyProfile,
  MiddlewareBaseResponse,
  MonetaryPointsData,
  PaginatedDto,
  ProfileLoyaltyAuditLog,
  ProfileLoyaltyAuditLogLine,
  RoomPointsData,
  StatusPointsData,
} from '@ireckonu/shared-ui';

import {
  AttributeDefinitionQuestionOptionApi,
  AttributeQuestion,
  FfpApiInterface,
  GroupedAttributes,
  LoyaltyApiInterface,
  MonetaryPointsDetails,
  PointTransaction,
  PointTransactionsPage,
  RoomNightsDetails,
  StatusPointsDetails,
  TierThreshold,
  VoucherApiInterface,
  VoucherCardApiInterface,
} from '../models';

function getPreferredEnrollment(
  enrollments: LoyaltyEnrollment[] | null | undefined,
): LoyaltyEnrollment | undefined {
  return (
    enrollments?.find(
      (enrollment) => enrollment.Status === LoyaltyEnrollmentStatus.Enrolled,
    ) ??
    enrollments?.find(
      (enrollment) => enrollment.Status === LoyaltyEnrollmentStatus.Pending,
    ) ??
    enrollments?.[enrollments.length - 1]
  );
}

@Injectable({ providedIn: 'root' })
export class LoyaltyMappingService {
  mapToLoyaltyDetails(
    response: MiddlewareBaseResponse<PaginatedDto<LoyaltyProfile>>,
  ): LoyaltyApiInterface {
    const item = response.Data?.Items?.[0];

    if (!item) {
      return this.getEmptyLoyaltyDetails();
    }

    const enrollment = getPreferredEnrollment(item.Enrollments);

    return {
      // IDs for subsequent API calls
      loyaltyProfileId: item.Id ?? null,
      enrollmentId: enrollment?.Id ?? 0,
      oneHarmonyId: enrollment?.Program?.Id ?? 0,
      // Balance fields
      currencyPoints: this.getBalanceAmount(enrollment, 'monetary'),
      statusPoints: this.getBalanceAmount(enrollment, 'status'),
      statusNights: this.getBalanceAmount(enrollment, 'room-nights'),
      pointsNextTier: 0, // To be calculated or fetched separately
      nightsNextTier: 0, // To be calculated or fetched separately

      // Membership Details
      loyaltyTier: enrollment?.Tier?.Name ?? 'N/A',
      membershipNumber: enrollment?.Code ?? 'N/A',
      enrollmentDate: enrollment?.CreatedAt ?? null,
      statusValidityDate: enrollment?.TierExpiryDate ?? null,
      enrollmentSource: enrollment?.EnrollmentSource ?? 'N/A',
      enrolledBy: enrollment?.EnrolledBy ?? 'N/A',
      membershipStatus: enrollment?.Status ?? null,
      physicalCardLetter: enrollment?.PhysicalCardIssueDate ? 'Yes' : 'No',
      physicalCardIssue: enrollment?.PhysicalCardIssueDate ?? null,
      earningPreference: item.EarningPreference ?? 'N/A',
      programName: enrollment?.Program?.Name ?? 'N/A',

      // Legacy fields
      expiryDate: enrollment?.TierExpiryDate ?? null,
      cardIssueDate: enrollment?.PhysicalCardIssueDate ?? null,
      cardOptInOut: 'N/A',
      pointsMiles: item.EarningPreference ?? 'Points',

      // Collections
      vouchers: this.mapVouchers(item),
      cards: this.mapCards(item),
      ffps: this.mapFfps(item),
      rawAttributes: this.mapRawAttributes(item),
    };
  }

  private getBalanceAmount(
    enrollment: LoyaltyEnrollment | undefined,
    pointTypeCode: string,
  ): number {
    return (
      enrollment?.Balances?.find((b) => b.PointTypeCode === pointTypeCode)
        ?.Amount ?? 0
    );
  }

  private mapVouchers(item: LoyaltyProfile): VoucherApiInterface[] {
    return (
      item.Attributes?.filter((attr) => attr.AttributeDefinitionId === 6).map(
        (attr) => ({
          id: String(attr.Id),
          title: (attr.Answers['VoucherType'] as string) ?? 'Voucher',
          hotel: 'N/A',
          count: Number(attr.Answers['Value']) || 1,
          expiryDate: (attr.Answers['LastUpdatedDate'] as string) ?? null,
        }),
      ) ?? []
    );
  }

  private mapCards(item: LoyaltyProfile): VoucherCardApiInterface[] {
    return (
      item.Attributes?.filter((attr) => attr.AttributeDefinitionId === 2).map(
        (attr) => ({
          id: String(attr.Id),
          title: (attr.Answers['CardType'] as string) ?? 'Card',
          type: (attr.Answers['CardTier'] as string) || 'N/A',
          voucher: 0,
          expiryDate: (attr.Answers['CardTierStartDate'] as string) ?? null,
        }),
      ) ?? []
    );
  }

  private mapFfps(item: LoyaltyProfile): FfpApiInterface[] {
    const ffps: FfpApiInterface[] =
      item.FrequentFlyerEnrollments?.map((ffp) => ({
        id: String(ffp.Id),
        airline: ffp.Airline?.Name ?? 'N/A',
        firstName: ffp.FirstName ?? 'N/A',
        lastName: ffp.LastName ?? 'N/A',
        ffpNumber: ffp.FrequentFlyerNumber ?? 'N/A',
        isPrimary: ffp.IsPrimary ?? false,
      })) ?? [];

    // Sort: Primary first, then alphabetically by airline
    return ffps.sort((a, b) => {
      if (a.isPrimary !== b.isPrimary) {
        return a.isPrimary ? -1 : 1;
      }
      return a.airline.localeCompare(b.airline);
    });
  }

  private mapRawAttributes(item: LoyaltyProfile): Array<{
    id: number;
    attributeDefinitionId: number;
    answers: Record<string, unknown>;
  }> {
    return (
      item.Attributes?.map((attr) => ({
        id: attr.Id,
        attributeDefinitionId: attr.AttributeDefinitionId,
        answers: attr.Answers,
      })) ?? []
    );
  }

  private getEmptyLoyaltyDetails(): LoyaltyApiInterface {
    return {
      loyaltyProfileId: 0,
      enrollmentId: 0,
      oneHarmonyId: 0,
      currencyPoints: 0,
      statusPoints: 0,
      statusNights: 0,
      pointsNextTier: 0,
      nightsNextTier: 0,
      loyaltyTier: 'N/A',
      membershipNumber: 'N/A',
      enrollmentDate: null,
      statusValidityDate: null,
      enrollmentSource: 'N/A',
      enrolledBy: 'N/A',
      membershipStatus: null,
      physicalCardLetter: 'N/A',
      physicalCardIssue: null,
      earningPreference: 'N/A',
      programName: 'N/A',
      expiryDate: null,
      cardIssueDate: null,
      cardOptInOut: 'N/A',
      pointsMiles: 'N/A',
      vouchers: [],
      cards: [],
      ffps: [],
      rawAttributes: [],
    };
  }

  mapToMonetaryPointsDetails(
    response: MiddlewareBaseResponse<MonetaryPointsData>,
  ): MonetaryPointsDetails {
    const data = response.Data;

    return {
      totalPoints: data?.TotalPoints ?? 0,
      basePoints: data?.BasePoints ?? 0,
      bonusPoints: data?.BonusPoints ?? 0,
      promotionPoints: data?.PromotionPoints ?? 0,
      goodWillPoints: data?.GoodWillPoints ?? 0,
      expiryDate: data?.ExpiryDate ?? null,
    };
  }

  mapToStatusPointsDetails(
    response: MiddlewareBaseResponse<StatusPointsData>,
  ): StatusPointsDetails {
    const data = response.Data;

    return {
      currentTier: data?.CurrentTier ?? 'N/A',
      totalPoints: data?.TotalPoints ?? 0,
      carryOverFromPreviousYear: data?.CarryOverFromPreviousYear ?? 0,
      carryOverFromTwoYearsAgo: data?.CarryOverFromTwoYearsAgo ?? 0,
      pointsToNextTier: data?.PointsToNextTier ?? 0,
      eligiblePointsBalanceLastYear: data?.EligiblePointsBalanceLastYear ?? 0,
    };
  }

  mapToRoomNightsDetails(
    response: MiddlewareBaseResponse<RoomPointsData>,
  ): RoomNightsDetails {
    const data = response.Data;
    const roomNightsToNextTier = this.calculateNightsToNextTier(
      data?.Total ?? 0,
      data?.TierThresholds ?? [],
    );

    return {
      totalCurrentYear: data?.Total ?? 0,
      totalLastYear: data?.LastYearTotal ?? 0,
      roomNightsToNextTier,
    };
  }

  private calculatePointsToNextTier(
    currentPoints: number,
    currentTier: string,
    thresholds: TierThreshold[],
  ): number {
    const sortedThresholds = [...thresholds].sort((a, b) => a.Min - b.Min);
    const currentTierIndex = sortedThresholds.findIndex(
      (t) => t.TierCode === currentTier,
    );

    if (
      currentTierIndex === -1 ||
      currentTierIndex === sortedThresholds.length - 1
    ) {
      return 0;
    }

    const nextTier = sortedThresholds[currentTierIndex + 1];
    return Math.max(0, nextTier.Min - currentPoints);
  }

  private calculateNightsToNextTier(
    currentNights: number,
    thresholds: TierThreshold[],
  ): number {
    const sortedThresholds = [...thresholds].sort((a, b) => a.Min - b.Min);
    const currentTierIndex = sortedThresholds.findIndex(
      (t) => currentNights >= t.Min,
    );

    // Find the next tier that requires more nights
    for (let i = currentTierIndex + 1; i < sortedThresholds.length; i++) {
      if (sortedThresholds[i].Min > currentNights) {
        return sortedThresholds[i].Min - currentNights;
      }
    }

    return 0;
  }

  mapToPointTransactions(
    response: MiddlewareBaseResponse<PaginatedDto<ProfileLoyaltyAuditLog>>,
  ): PointTransactionsPage {
    const data = response.Data;
    const items = data?.Items ?? [];
    return {
      items: items.map((item) => this.mapPointTransactionItem(item)),
      page: data?.Page ?? 1,
      pageSize: data?.PageSize ?? 20,
      totalItems: data?.TotalItems ?? 0,
    };
  }

  private mapPointTransactionItem(
    item: ProfileLoyaltyAuditLog,
  ): PointTransaction {
    const lines = item.Lines ?? [];

    return {
      id: item.Id,
      location: item.Metadata?.PropertyCode ?? 'N/A',
      propertyType: item.Metadata?.PropertyType ?? 'Hotel',
      transactionDate: item.TransactionDate ?? null,
      department: item.Metadata?.Department ?? 'N/A',
      transactionType: item.Kind ?? 'N/A',
      roomNightsPoints: this.getPointAmount(lines, 'room-nights'),
      monetaryPoints: this.getPointAmount(lines, 'monetary'),
      statusPoints: this.getPointAmount(lines, 'status'),
      // Expanded details
      arrivalDate: item.Metadata?.ArrivalDate ?? null,
      departureDate: item.Metadata?.DepartureDate ?? null,
      reference: item.Reference ?? 'N/A',
      revenue: item.Metadata?.Revenue ?? 0,
      basePoints: this.getPointAmountByReason(lines, 'Base'),
      bonusPoints: this.getPointAmountByReason(lines, 'Bonus'),
      promotionPoints: this.getPointAmountByReason(lines, 'Promotion'),
      remarks: item.Remarks ?? 'N/A',
      sessionCode: item.SessionCode ?? 'N/A',
      ffpNumber: item.FrequentFlyerNumber ?? 'N/A',
      airline: item.Airline?.Name ?? 'N/A',
      miles: this.getPointAmount(lines, 'miles'),
    };
  }

  private getPointAmount(
    lines: ProfileLoyaltyAuditLogLine[],
    pointType: string,
  ): number {
    return lines
      .filter((l) => l.PointTypeCode?.toLowerCase() === pointType.toLowerCase())
      .reduce((sum, l) => sum + (l.Amount ?? 0), 0);
  }

  private getPointAmountByReason(
    lines: ProfileLoyaltyAuditLogLine[],
    reason: string,
  ): number {
    return lines
      .filter(
        (l) =>
          l.EarningReasonCode?.toLowerCase() === reason.toLowerCase() &&
          l.PointTypeCode === 'monetary',
      )
      .reduce((sum, l) => sum + (l.Amount ?? 0), 0);
  }

  mapToGroupedAttributes(
    rawAttributes: Array<{
      id: number;
      attributeDefinitionId: number;
      answers: Record<string, unknown>;
    }>,
    definitions: LoyaltyAttributeDefinitionDto[],
  ): GroupedAttributes[] {
    if (!rawAttributes || rawAttributes.length === 0) {
      return [];
    }

    const grouped = new Map<number, GroupedAttributes>();

    for (const attr of rawAttributes) {
      const definition = definitions.find(
        (d) => d.Id === attr.attributeDefinitionId,
      );
      if (!definition) {
        continue;
      }

      if (!grouped.has(definition.Id)) {
        grouped.set(definition.Id, {
          categoryName: definition.Name,
          categoryCode: definition.Code,
          instances: [],
        });
      }

      const questions = this.mapAnswersToQuestions(
        attr.answers,
        definition.Questions,
      );

      const group = grouped.get(definition.Id);
      if (group) {
        group.instances.push({
          id: attr.id,
          questions,
        });
      }
    }

    return Array.from(grouped.values());
  }

  private mapAnswersToQuestions(
    answers: Record<string, unknown>,
    questions: LoyaltyAttributeDefinitionQuestionDto[],
  ): AttributeQuestion[] {
    return questions
      .filter((q) => q.PresentationDetails?.IsVisible !== false)
      .map((question) => {
        const rawValue = answers[question.Key];
        const displayValue = this.formatAnswerValue(rawValue, question);

        return {
          key: question.Key,
          label: question.Text,
          value: displayValue,
          uiType: question.PresentationDetails?.UiType ?? 'Text',
        };
      })
      .filter(
        (q) => q.value !== '' && q.value !== null && q.value !== undefined,
      );
  }

  private formatAnswerValue(
    value: unknown,
    question: LoyaltyAttributeDefinitionQuestionDto,
  ): string {
    if (value === null || value === undefined || value === '') {
      return '';
    }

    const stringValue = this.toStringValue(value);

    if (
      question.Kind === 'SingleChoice' &&
      question.AvailableOptions &&
      question.AvailableOptions.length > 0
    ) {
      const option = question.AvailableOptions.find(
        (opt: AttributeDefinitionQuestionOptionApi) => opt.Value === value,
      );
      return option?.Text ?? stringValue;
    }

    if (question.Kind === 'YesNo') {
      return value === true || value === 'true' ? 'Yes' : 'No';
    }

    return stringValue;
  }

  private toStringValue(value: unknown): string {
    if (typeof value === 'string') {
      return value;
    }
    if (typeof value === 'number' || typeof value === 'boolean') {
      return String(value);
    }
    return '';
  }
}
